import 'react-toastify/dist/ReactToastify.css';
import '@/styles/globals.scss';
import '@/styles/datepicker.scss';

import { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { appWithTranslation } from 'next-i18next';
import { ReactElement, ReactNode } from 'react';
import { ToastContainer } from 'react-toastify';

import { HealthStatusOverlay } from '@/components/HealthStatusOverlay';
import MaintenanceBar from '@/components/MaintananceBar';
import PageTitle from '@/components/PageTitle';
import { Required2FAOverlay } from '@/components/Required2FAOverlay';
import ActivityCartContextProvider from '@/context/ActivityCartContext/ActivityCartContextProvider';
import ApiContextProvider from '@/context/ApiContext/ApiContextProvider';
import DashboardContextProvider from '@/context/DashboardContext/DashboardContextProvider';
import DeviceIdentifierContextProvider from '@/context/DeviceIdentifierContext/DeviceIdentifierContextProvider';
import HealthStatusContextProvider from '@/context/HealthStatusContext/HealthStatusContextProvider';
import ModalContextProvider from '@/context/ModalContext/ModalContextProvider';
import PreviousRouteContextProvider from '@/context/PreviousRouteContext/PreviousRouteContextProvider';
import QueryClientContextProvider from '@/context/QueryClientContext/QueryClientContextProvider';
import classNames from '@/helpers/classNames';
import dmSans from '@/utils/typography';

import styles from './_app.module.scss';

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<
  P,
  IP
> & {
  getLayout?: (page: ReactElement, pageProps?: P) => ReactNode;
};

function App({
  Component,
  pageProps,
}: AppProps<{ session: null }> & { Component: NextPageWithLayout }) {
  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <main className={classNames([dmSans.className, dmSans.variable])}>
      <PageTitle />
      <ApiContextProvider>
        <QueryClientContextProvider>
          <ModalContextProvider>
            <PreviousRouteContextProvider>
              <HealthStatusContextProvider>
                <DeviceIdentifierContextProvider>
                  <DashboardContextProvider>
                    <ActivityCartContextProvider>
                      <div className={styles.container}>
                        <MaintenanceBar />
                        {getLayout(<Component {...pageProps} />, pageProps)}
                      </div>
                      <HealthStatusOverlay />
                      <Required2FAOverlay />
                      <ToastContainer
                        hideProgressBar={true}
                        position={'bottom-left'}
                      />
                    </ActivityCartContextProvider>
                  </DashboardContextProvider>
                </DeviceIdentifierContextProvider>
              </HealthStatusContextProvider>
            </PreviousRouteContextProvider>
          </ModalContextProvider>
        </QueryClientContextProvider>
      </ApiContextProvider>
    </main>
  );
}

export default appWithTranslation(App);
